// this is the big marketing homepage - hero, press logos, demo video, features, pricing, footer
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
// our custom hook that fades things in as you scroll
import { useScrollReveal } from '../hooks/useScrollReveal';
import { usePageMeta } from '../hooks/usePageMeta';
import Icon from '../components/Icon';
import './LandingPage.css';

export default function LandingPage() {
  // the homepage's search listing - leads with the terms people type
  usePageMeta({
    title: 'GPTDisguise: Use ChatGPT & Claude Disguised as a Google Doc',
    description: 'A free Chrome and Firefox extension that wraps ChatGPT, Claude and Gemini in a pixel-perfect Google Docs interface. Keep your screen private in open-plan offices and cafés. Nothing leaves your browser.',
    path: '/'
  });

  // turn on the scroll-fade animations for this page
  useScrollReveal();
  const navigate = useNavigate();
  // tracks whether we've scrolled down a bit (so the nav bar can go solid)
  const [scrolled, setScrolled] = useState(false);
  // the logged-in user, if any - changes what the nav shows
  const [user, setUser] = useState(null);
  // a ref to the demo <video> element (not really used much, but handy to have)
  const videoRef = useRef(null);

  // watch the page scroll and flip 'scrolled' true once we're past 40px down
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    // passive:true tells the browser we won't block scrolling, keeps it smooth
    window.addEventListener('scroll', handleScroll, { passive: true });
    // clean up the listener when we leave the page
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // figure out if someone's logged in, and keep it updated if they log in/out
  useEffect(() => {
    // ask once on load
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
    // then subscribe to future changes (login, logout, token refresh)
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    // unsubscribe when the page unmounts so we don't leak
    return () => listener.subscription.unsubscribe();
  }, []);

  // log out and immediately reflect that in the ui
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  // smooth-scroll down to the demo section when they click the little arrow
  const scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  // name + initials for the avatar button in the nav (only shown when logged in)
  const displayName = user?.user_metadata?.full_name || user?.email;
  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  // the list of feature cards shown in the features section. easier to keep as data
  // and loop over than to copy-paste the same markup seven times.
  const features = [
    { icon: 'disguise', title: 'Pixel-perfect disguise', desc: 'Renders ChatGPT inside a flawless Google Docs UI - toolbar, title bar, sidebar and all. Indistinguishable from the real thing.' },
    { icon: 'sparkle', title: 'Now works with Claude', desc: 'Use Claude behind the same flawless disguise, right alongside ChatGPT. Switch between models without dropping cover. Premium feature.' },
    { icon: 'zap', title: 'One-click activation', desc: 'Toggle the disguise instantly from the extension toolbar. Works on any page without a redirect.' },
    { icon: 'shield', title: 'Nothing leaves your browser', desc: 'Pure CSS and JavaScript. No server, no tracking, no data collection - ever.' },
    { icon: 'palette', title: 'Microsoft, Notion & Claude themes', desc: 'Disguise as Microsoft Word, Notion, or Claude - now live for Premium. More themes coming soon.' },
    { icon: 'command', title: 'Keyboard shortcuts', desc: 'All standard Docs shortcuts work, including toggle, hide, and focus mode. Premium feature.' },
    { icon: 'layers', title: 'Session memory', desc: 'Saves and restores your chat sessions per tab, automatically. Premium feature.' },
  ];

  // the "as featured on" press logos. each has its own pixel height because every
  // logo image is a different shape, and we want them all to look the same weight
  // sitting next to each other.
  const press = [
    { name: 'TechRadar', logo: '/press/techradar.svg', height: 20, href: 'https://www.techradar.com/ai-platforms-assistants/chatgpt/i-felt-weird-using-chatgpt-in-public-so-i-tried-this-extension-that-disguises-it-as-a-google-doc' },
    { name: 'Government Technology', logo: '/press/govtech.svg', height: 36, href: 'https://www.govtech.com/question-of-the-day/how-does-this-browser-extension-disguise-chatgpt-while-youre-using-it' },
    { name: 'Mezha', logo: '/press/mezha.png', height: 15, href: 'https://mezha.ua/en/news/chrome-rozshirennya-maskuye-chatgpt-pid-google-docs-312291/' },
    { name: 'PiunikaWeb', logo: '/press/piunikaweb.svg', height: 17, href: 'https://piunikaweb.com/2026/06/15/chrome-extension-disguises-chatgpt-gemini-claude-as-google-docs/' },
    { name: 'TechTiper', logo: '/press/techtiper.svg', height: 21, href: 'https://www.techtiper.com/shy-about-ai-chatbots-chrome-extension-gptdisguise/' },
    { name: 'inkl', logo: '/press/inkl.svg', height: 22, href: 'https://www.inkl.com/news/i-felt-weird-using-chatgpt-in-public-so-i-tried-this-extension-that-disguises-it-as-a-google-doc' },
  ];

  // the "coming soon" roadmap pills. status controls the little colored tag on each
  const roadmap = [
    { label: 'Gemini support', icon: 'sparkle', status: 'alpha' },
    { label: 'More disguise themes', icon: 'palette', status: 'soon' },
    { label: 'Microsoft Excel disguise', icon: 'grid', status: 'soon' },
    { label: 'Session memory', icon: 'layers', status: 'soon' },
    // this one's actually a link since the firefox version is live in beta
    { label: 'Firefox extension', icon: 'globe', status: 'beta', href: 'https://addons.mozilla.org/firefox/addon/gptdisguise/' },
  ];

  // the faq entries. these have to stay word-for-word in step with the FAQPage
  // structured data in public/index.html - see the note down at that section.
  const faqs = [
    {
      q: 'What is GPTDisguise?',
      a: 'GPTDisguise is a browser extension that renders ChatGPT inside a pixel-perfect replica of the Google Docs interface - toolbar, title bar and sidebar included. You use the AI normally, but a passing glance at your screen shows a document rather than a chat window.'
    },
    {
      q: 'Is GPTDisguise free?',
      a: 'Yes. The free plan covers the Google Docs disguise with ChatGPT and needs no account. Premium is $4 per month and adds Claude, Gemini (alpha), Microsoft Word and Notion themes, keyboard shortcuts and session memory. A one-time $20 lifetime plan includes every Premium feature plus future ones.'
    },
    {
      q: 'Does GPTDisguise send my conversations anywhere?',
      a: 'No. The disguise is pure CSS and JavaScript applied in your own browser. There is no GPTDisguise server in the loop, no tracking and no data collection. Your conversation goes to ChatGPT or Claude exactly as it would without the extension.'
    },
    {
      q: 'Does GPTDisguise work on Firefox?',
      a: 'Yes. The Chrome version is the primary release, and a Firefox build is available in beta on Mozilla Add-ons.'
    },
    {
      q: 'Which AI models does GPTDisguise support?',
      a: 'ChatGPT is supported on every plan. Claude is available on Premium, and Gemini support is in alpha for Premium users.'
    }
  ];

  return (
    <div className="landing">
      {/* NAV */}
      {/* the sticky top nav. adds a 'scrolled' class once you scroll so it can go solid */}
      <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <a href="/" className="nav-logo">
          <img src="/icon.png" alt="GPTDisguise icon" className="nav-icon" />
          GPT<span>Disguise</span>
        </a>
        <div className="nav-links">
          {/* these three just jump to sections further down the page */}
          <a href="#features">Features</a>
          <a href="#roadmap">Roadmap</a>
          <a href="#pricing">Pricing</a>
          {/* if logged in, show avatar + sign out. if not, show a sign up button.
              that button carries ?mode=signup so the auth page opens on the
              Create account tab rather than the Sign in one. */}
          {user ? (
            <div className="nav-user">
              <button className="nav-avatar" onClick={() => navigate('/dashboard')} title={displayName}>
                {initials}
              </button>
              <button className="nav-signout" onClick={handleSignOut}>Sign out</button>
            </div>
          ) : (
            <button className="nav-signin" onClick={() => navigate('/signin?mode=signup')}>Sign up</button>
          )}
        </div>
      </nav>

      {/* HERO */}
      {/* the big top section with the headline and the main call-to-action buttons */}
      <section className="hero">
        {/* three decorative background layers: two glow orbs and a faint grid */}
        <div className="hero-bg-orb hero-bg-orb--blue" />
        <div className="hero-bg-orb hero-bg-orb--green" />
        <div className="hero-bg-grid" />
        {/* the little pill above the headline with a pulsing dot */}
        <div className="hero-eyebrow">
          <span className="eyebrow-dot" />
          Now available for Chrome
        </div>
        <h1 className="hero-h1">
          ChatGPT,<br />
          <em>dressed as a</em><br />
          Google Doc.
        </h1>
        <p className="hero-sub">
          A Chrome extension that wraps ChatGPT, and now Claude, in a flawless
          Google Docs interface. Look productive. Actually be productive.
        </p>
        {/* the row of main buttons */}
        <div className="hero-actions">
          {/* primary button: install from the chrome web store (opens in new tab) */}
          <a href="https://chromewebstore.google.com/detail/gptdisguise/bccghhjlejbkimijdkdfoboamcjgpefg" target="_blank" rel="noreferrer" className="btn-chrome">
            <ChromeIcon />
            Add to Chrome - it's free
          </a>
          {/* the firefox version, still in beta */}
          <a href="https://addons.mozilla.org/firefox/addon/gptdisguise/" target="_blank" rel="noreferrer" className="btn-firefox">
            <Icon name="globe" size={18} className="btn-firefox-icon" />
            Add to Firefox
            <span className="btn-firefox-beta">Beta</span>
          </a>
          {/* only show a "sign in" ghost button if they're not already logged in */}
          {!user && (
            <button className="btn-ghost" onClick={() => navigate('/signin')}>
              Sign in
            </button>
          )}
        </div>
        {/* little trust line under the buttons */}
        <p className="hero-meta">Chrome 5k+ &nbsp;·&nbsp; 0.3 MB &nbsp;·&nbsp; No account needed for free plan</p>
        {/* the bouncing down-arrow that scrolls you to the demo */}
        <button className="scroll-arrow" onClick={scrollToDemo} aria-label="Scroll to demo">
          <span className="scroll-arrow-label">See the demo</span>
          <div className="arrow-bounce">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4v12M4 10l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </button>
      </section>

      {/* PRESS */}
      {/* the row of news outlet logos that covered us. loops over the 'press' list above */}
      <section className="press-section" aria-label="Press coverage">
        <p className="press-eyebrow reveal">As featured on</p>
        <ul className="press-list reveal" style={{ transitionDelay: '0.08s' }}>
          {press.map((p) => (
            <li key={p.name}>
              {/* each logo links to the article, and uses its custom --logo-h height */}
              <a href={p.href} target="_blank" rel="noreferrer" className="press-item" title={p.name}>
                <img src={p.logo} alt={p.name} style={{ '--logo-h': `${p.height}px` }} loading="lazy" />
              </a>
            </li>
          ))}
          <li className="press-more">+ more</li>
        </ul>
      </section>

      {/* DEMO VIDEO */}
      {/* the looping demo, framed inside a fake browser window for effect */}
      <section id="demo" className="demo-section">
        <div className="demo-label reveal"><span className="demo-badge">Live demo</span></div>
        <div className="demo-frame-wrap reveal" style={{ transitionDelay: '0.1s' }}>
          {/* the fake browser chrome: traffic lights, url bar, etc - pure decoration */}
          <div className="browser-chrome">
            <div className="traffic-lights">
              <span className="tl tl-r" /><span className="tl tl-y" /><span className="tl tl-g" />
            </div>
            <div className="url-bar">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}>
                <path d="M6 1a5 5 0 1 0 0 10A5 5 0 0 0 6 1zm0 9A4 4 0 1 1 6 2a4 4 0 0 1 0 8z" fill="#34a853"/>
              </svg>
              chatgpt.com
            </div>
            <div className="browser-actions"><span>⋯</span></div>
          </div>
          {/* the actual video: autoplays, loops, muted (browsers require muted to autoplay) */}
          <div className="video-container">
            <video ref={videoRef} src="/demo.mp4" autoPlay loop muted playsInline className="demo-video" />
            {/* little "active" badge overlaid on the video */}
            <div className="video-overlay-badge">
              <span className="green-dot" />GPTDisguise active
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {/* the grid of feature cards, built by looping over the 'features' list from up top */}
      <section id="features" className="features-section">
        <div className="section-header reveal">
          <p className="section-eyebrow">Features</p>
          <h2>Everything you need to<br /><em>stay undercover</em></h2>
        </div>
        <div className="features-grid">
          {features.map((f, i) => (
            // the tiny staggered transitionDelay makes them fade in one after another
            <div key={f.title} className="feat-card reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
              <div className="feat-icon"><Icon name={f.icon} size={20} /></div>
              <h3 className="feat-title">{f.title}</h3>
              <p className="feat-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ROADMAP */}
      {/* what's coming next - a heading on the left and a bunch of pills on the right */}
      <section id="roadmap" className="roadmap-section">
        <div className="roadmap-inner">
          <div className="reveal-left">
            <p className="section-eyebrow">Roadmap</p>
            <h2>Coming soon</h2>
            <p className="roadmap-sub">We're expanding GPTDisguise beyond ChatGPT. More AI models, more disguises.</p>
          </div>
          <div className="roadmap-pills reveal-right" style={{ transitionDelay: '0.1s' }}>
            {roadmap.map((item) => {
              // work out the tag text + css class from the item's status
              const statusLabel = item.status === 'alpha' ? 'Alpha' : item.status === 'beta' ? 'Beta' : 'Soon';
              const statusClass = `roadmap-soon${item.status === 'alpha' ? ' roadmap-alpha' : item.status === 'beta' ? ' roadmap-beta' : ''}`;
              // the inner content is the same whether it's a link or not, so build it once
              const content = (
                <>
                  <span className="roadmap-pill-icon"><Icon name={item.icon} size={17} /></span>
                  {item.label}
                  <span className={statusClass}>{statusLabel}</span>
                </>
              );
              // if the item has a href it's a clickable link, otherwise just a plain div
              return item.href ? (
                <a key={item.label} href={item.href} target="_blank" rel="noreferrer" className="roadmap-pill roadmap-pill--link">
                  {content}
                </a>
              ) : (
                <div key={item.label} className="roadmap-pill">
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      {/* three pricing cards: free, premium monthly, and lifetime */}
      <section id="pricing" className="pricing-section">
        <div className="section-header reveal">
          <p className="section-eyebrow">Pricing</p>
          <h2>Simple, honest pricing</h2>
        </div>
        <div className="pricing-grid">
          {/* FREE plan card. 'yes'/'no' classes on the <li>s draw a check or an x */}
          <div className="plan-card reveal" style={{ transitionDelay: '0.05s' }}>
            <div className="plan-name">Free</div>
            <div className="plan-price">$0<span>/forever</span></div>
            <ul className="plan-features">
              <li className="yes">Google Docs disguise</li>
              <li className="yes">ChatGPT support</li>
              <li className="no">Toggle keyboard shortcut</li>
              <li className="no">Claude, Microsoft & Notion themes</li>
              <li className="no">Session memory</li>
              <li className="no">Gemini (alpha)</li>
            </ul>
            <button className="plan-btn plan-btn-free" onClick={() => navigate('/signin')}>
              Get started free
            </button>
          </div>
          {/* PREMIUM card - highlighted as "most popular" */}
          <div className="plan-card plan-card--featured reveal" style={{ transitionDelay: '0.15s' }}>
            <div className="plan-featured-badge">Most popular</div>
            <div className="plan-name">Premium</div>
            <div className="plan-price">$4<span>/month</span></div>
            <ul className="plan-features">
              <li className="yes">Everything in Free</li>
              <li className="yes">Claude support</li>
              <li className="yes">Gemini support (alpha)</li>
              <li className="yes">Microsoft Word & Notion themes</li>
              <li className="yes">Toggle keyboard shortcuts</li>
              <li className="yes">Session memory (coming soon)</li>
              <li className="yes">More themes & disguises (coming soon)</li>
              <li className="yes">Priority support</li>
            </ul>
            {/* if logged in send them to the dashboard to pay, else to sign in first */}
            <button className="plan-btn plan-btn-pro" onClick={() => navigate(user ? '/dashboard' : '/signin')}>
              {user ? 'Upgrade to Premium' : 'Get Premium'}
            </button>
          </div>
          {/* LIFETIME card - pay once, flagged "best value" */}
          <div className="plan-card plan-card--lifetime reveal" style={{ transitionDelay: '0.25s' }}>
            <div className="plan-featured-badge plan-featured-badge--lifetime">Best value</div>
            <div className="plan-name">Lifetime</div>
            <div className="plan-price">$20<span>one-time</span></div>
            <ul className="plan-features">
              <li className="yes">Everything in Premium</li>
              <li className="yes">All future Premium features</li>
              <li className="yes">No recurring billing, ever</li>
              <li className="yes">Pay once, yours forever</li>
            </ul>
            <button className="plan-btn plan-btn-pro" onClick={() => navigate(user ? '/dashboard' : '/signin')}>
              Get Lifetime
            </button>
          </div>
        </div>
        <p className="pricing-note reveal" style={{ transitionDelay: '0.2s' }}>
          Cancel anytime · Billed securely via Stripe · No credit card needed for free plan
        </p>
      </section>

      {/* FAQ */}
      {/* these answers are duplicated as FAQPage structured data in
          public/index.html, which is what lets them show as a drop-down block
          under our google result. if you edit the wording here, edit it there
          too - schema that doesn't match the visible page breaks the rules. */}
      <section id="faq" className="faq-section">
        <div className="faq-inner">
          <div className="section-header faq-head reveal">
            <p className="section-eyebrow">FAQ</p>
            <h2>Common <em>questions.</em></h2>
          </div>
          <div className="faq-list">
            {faqs.map((item, i) => (
              // <details> gives us a working accordion with no javascript at all,
              // and crawlers can still read the answer while it's collapsed
              <details key={item.q} className="faq-item reveal" style={{ transitionDelay: `${0.05 * i}s` }}>
                <summary className="faq-q">
                  {item.q}
                  <span className="faq-chevron" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </summary>
                <p className="faq-a">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      {/* bottom of the page: logo, nav links, social icons, copyright */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-logo">
            <img src="/icon.png" alt="GPTDisguise" className="footer-icon" />
            GPT<span>Disguise</span>
          </div>
          <div className="footer-links">
            <a href="#features">Features</a>
            <a href="#roadmap">Roadmap</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            {/* real link to the static blog - gives the articles a route in
                from the homepage, which is how google finds them */}
            <a href="/blog/">Blog</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </div>
          {/* social links - each is just an inline svg logo */}
          <div className="footer-social">
            <a href="https://www.instagram.com/gptdisguise/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>
            <a href="https://www.youtube.com/@gptdisguise" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
              </svg>
            </a>
            <a href="https://www.tiktok.com/@gptdisguise" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.6 5.82a4.28 4.28 0 0 1-1.06-2.82h-3.3v12.9a2.59 2.59 0 0 1-2.6 2.5 2.6 2.6 0 0 1 0-5.2c.27 0 .53.04.78.12V9.92a5.9 5.9 0 0 0-.78-.05A5.92 5.92 0 1 0 15.34 16V9.42a7.56 7.56 0 0 0 4.45 1.42V7.54a4.28 4.28 0 0 1-3.19-1.72z"></path>
              </svg>
            </a>
          </div>
          {/* new Date().getFullYear() just keeps the copyright year current automatically */}
          <p className="footer-copy">© {new Date().getFullYear()} GPTDisguise · Cloak Studio · Not affiliated with OpenAI or Google</p>
        </div>
      </footer>
    </div>
  );
}

// the white chrome logo used on the "add to chrome" button. hardcoded svg, its own
// little component just to keep the big jsx above a bit cleaner.
function ChromeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="3.5" fill="white" />
      <path d="M9 5.5h7.26A8 8 0 1 0 9 17V5.5z" fill="none" stroke="white" strokeWidth="1.2" />
    </svg>
  );
}
